import React, { useState } from "react";
import {  Card,
  CardContent,
  Typography,
  Divider,
  Tabs,
  Tab,
  Box,
  alpha,
  useTheme,
  Chip,
  Stack,} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Info as InfoIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  DirectionsBoat as BoatIcon,
} from "@mui/icons-material";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const toLabel = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

const LabelValue = ({ label, value }) => (
  <Grid container spacing={1} sx={{ mb: 1.25 }}>
    <Grid size={5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid size={7}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value ?? "N/A"}
      </Typography>
    </Grid>
  </Grid>
);

const renderObjectRows = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  return Object.entries(obj).map(([key, value]) => {
    if (value && typeof value === "object") return null;
    return <LabelValue key={key} label={toLabel(key)} value={value} />;
  });
};

const ShipParticulars = () => {
  const { shipData, vesselInfo } = useShipSettings();
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  if (!shipData) return null;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const shipTitle =
    shipData?.name ||
    shipData?.configuration?.identifiers?.vesselName ||
    shipData?.baseSettings?.shipName ||
    "Ship Particulars";

  const cargoTanks = shipData?.configuration?.tankLayout?.cargoTanks || {};
  const ballastTanks = shipData?.configuration?.tankLayout?.ballastTanks || {};
  const contacts =
    shipData?.contacts || shipData?.ownerInformation || shipData?.contact || {};
  const owner =
    contacts?.owner || shipData?.ownerInformation?.owner || shipData?.owner;
  const operator =
    contacts?.operator ||
    shipData?.ownerInformation?.operator ||
    shipData?.operator;

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: { xs: 1, md: 3 } }}>
      {/* HEADER SECTION */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <BoatIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {shipTitle}
          </Typography>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 1 }}
          >
            Vessel Specification & Technical Data
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        elevation={0}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              "& .MuiTab-root": {
                py: 2,
                fontWeight: 700,
                fontSize: "0.85rem",
                textTransform: "uppercase",
              },
            }}
          >
            <Tab
              icon={<InfoIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="General Info"
            />
            <Tab
              icon={<SettingsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Static & Tanks"
            />
            <Tab
              icon={<BusinessIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Contacts & Owners"
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          {/* TAB 1: BASE SETTINGS */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Vessel Identifiers
                </Typography>
                <LabelValue label="Ship ID" value={shipData?.id} />
                {renderObjectRows(vesselInfo)}
              </Card>

              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Configuration Snapshot
                </Typography>
                <LabelValue
                  label="Cargo Tanks"
                  value={Object.keys(cargoTanks).length}
                />
                <LabelValue
                  label="Ballast Tanks"
                  value={Object.keys(ballastTanks).length}
                />
                <LabelValue
                  label="Data Source"
                  value={shipData?.configuration ? "New Configuration" : "Legacy"}
                />
              </Card>
            </Stack>
          </TabPanel>

          {/* TAB 2: STATIC SETTINGS */}
          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Cargo Tanks
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(cargoTanks).length ? (
                    Object.entries(cargoTanks).map(([tankName, details]) => (
                      <Chip
                        key={tankName}
                        label={`${tankName.toUpperCase()} ${
                          details?.cube ? `• ${details.cube} m3` : ""
                        }`}
                        variant="outlined"
                        color="primary"
                        size="small"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cargo tanks found.
                    </Typography>
                  )}
                </Box>
              </Card>

              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Ballast Tanks
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(ballastTanks).length ? (
                    Object.entries(ballastTanks).map(([tankName, details]) => (
                      <Chip
                        key={tankName}
                        label={`${tankName.toUpperCase()} ${
                          details?.cube ? `• ${details.cube} m3` : ""
                        }`}
                        variant="outlined"
                        size="small"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No ballast tanks found.
                    </Typography>
                  )}
                </Box>
              </Card>
            </Stack>
          </TabPanel>

          {/* TAB 3: OWNER & OPERATOR */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Registered Owner
                </Typography>
                {owner ? (
                  renderObjectRows(owner)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Owner information is not available in current ship data.
                  </Typography>
                )}
              </Card>
              <Divider />
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
                  Technical Operator
                </Typography>
                {operator ? (
                  renderObjectRows(operator)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Operator information is not available in current ship data.
                  </Typography>
                )}
              </Card>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ShipParticulars;
